import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

import type {
  AuthContextValue,
  AuthLoginData,
  AuthUserDataUpdate,
  CameraLock,
  ProjectionType,
  UserRoleLevel,
} from '../types';
import logger from '../utils/logger';

/**
 * Authentication Context for the LifeFinder application
 * Provides centralized state management for user authentication,
 * user data, and permissions throughout the application.
 */
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Props for AuthProvider component
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Custom hook to access authentication context
 * Must be used within an AuthProvider component
 *
 * @returns Authentication context with user data and methods
 * @throws Error if used outside of AuthProvider
 */
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * Wraps the application to provide authentication state and methods
 * Automatically syncs with localStorage for persistence
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Core authentication state
  const [token, setToken] = useState<string | null>(() => window.localStorage.getItem('token'));
  const [user, setUser] = useState<string | null>(() => window.localStorage.getItem('username'));
  const [account, setAccount] = useState<string | null>(null);
  const [rights, setRights] = useState<string | null>(() => window.localStorage.getItem('rights'));
  const [projection, setProjection] = useState<ProjectionType | string | null>(() =>
    window.localStorage.getItem('projection')
  );
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [defaultCenter, setDefaultCenter] = useState<{
    lat: number;
    lng: number;
    zoom: number | null;
  } | null>(() => {
    const stored = window.localStorage.getItem('default-center');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  });
  const [cameraLock, setCameraLock] = useState<CameraLock | null>(() => {
    const stored = window.localStorage.getItem('camera-lock');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  });
  const [operatingGuide, setOperatingGuide] = useState<string | null>(() =>
    window.localStorage.getItem('operating-guide')
  );
  // Per-session override: when true, the locked user has temporarily unlocked the map.
  // Intentionally in-memory only (never persisted) so an F5 refresh or logout resets it
  // and the configured lock re-applies.
  const [cameraLockOverride, setCameraLockOverride] = useState<boolean>(false);

  // Automatically sync authentication token with localStorage
  useEffect(() => {
    if (token) {
      window.localStorage.setItem('token', token);
      setIsAuthenticated(true);
    } else {
      window.localStorage.removeItem('token');
      setIsAuthenticated(false);
    }
  }, [token]);

  // Automatically sync map projection preference with localStorage
  useEffect(() => {
    if (projection && (projection === 'timespace' || projection === 'sideways')) {
      window.localStorage.setItem('projection', projection);
    } else if (projection === null) {
      window.localStorage.removeItem('projection');
    }
    // Invalid projection values are ignored (not saved to localStorage)
  }, [projection]);

  // Persist defaultCenter to localStorage so it survives page refresh
  useEffect(() => {
    if (defaultCenter) {
      window.localStorage.setItem('default-center', JSON.stringify(defaultCenter));
    } else {
      window.localStorage.removeItem('default-center');
    }
  }, [defaultCenter]);

  // Persist operatingGuide to localStorage so the right guide shows before the next login
  useEffect(() => {
    if (operatingGuide) {
      window.localStorage.setItem('operating-guide', operatingGuide);
    } else {
      window.localStorage.removeItem('operating-guide');
    }
  }, [operatingGuide]);

  // Persist cameraLock to localStorage so the restriction applies before the next login
  useEffect(() => {
    if (cameraLock) {
      window.localStorage.setItem('camera-lock', JSON.stringify(cameraLock));
    } else {
      window.localStorage.removeItem('camera-lock');
    }
  }, [cameraLock]);

  /**
   * Check if user has super administrator rights
   * SuperAdmin has all system-level privileges including user management
   */
  const isSuperAdmin = (): boolean => {
    return typeof rights === 'string' && rights.includes('S');
  };

  /**
   * Check if user has administrator rights
   * SuperAdmin ('S') automatically has admin rights
   */
  const isAdmin = (): boolean => {
    return typeof rights === 'string' && (rights.includes('A') || rights.includes('S'));
  };

  /**
   * Check if user has checker rights (can check in at checkpoints)
   */
  const isChecker = (): boolean => {
    // SuperAdmins have all rights including checker
    return typeof rights === 'string' && (rights.includes('C') || rights.includes('S'));
  };

  /**
   * Check if user can access filter functionality
   * Requires F (Filterer) right AND at least one of S (SuperAdmin), A (Admin), or C (Checker)
   */
  const showFilters = (): boolean => {
    // Must have F right AND at least one of S, A, or C
    return (
      typeof rights === 'string' &&
      rights.includes('F') &&
      (rights.includes('S') || rights.includes('A') || rights.includes('C'))
    );
  };

  /**
   * Check if user can manage other users
   * Only SuperAdmin can manage Admin users and above
   */
  const canManageUsers = (): boolean => {
    return isSuperAdmin();
  };

  /**
   * Check if user can access system configuration
   */
  const canAccessSystemConfig = (): boolean => {
    return isSuperAdmin();
  };

  /**
   * Check if user can view audit logs and system analytics
   */
  const canViewAuditLogs = (): boolean => {
    return isSuperAdmin();
  };

  /**
   * Get user role hierarchy level (higher number = more privileges).
   * @returns Role level: 4=SuperAdmin, 3=Admin, 2=Checker, 0=User
   */
  const getUserRoleLevel = (): UserRoleLevel => {
    if (isSuperAdmin()) return 4;
    if (isAdmin()) return 3;
    if (isChecker()) return 2;
    return 0;
  };

  /**
   * Get user's highest role name for display
   * @returns Role name in Swedish
   */
  const getUserRoleName = (): string => {
    if (isSuperAdmin()) return 'Systemadministratör';
    if (isAdmin()) return 'Administratör';
    if (isChecker()) return 'Kontrollant';
    return 'Användare';
  };

  /**
   * Log in user with authentication data
   * Stores all user data in state and localStorage
   */
  const login = (authData: AuthLoginData): void => {
    logger.debug('AuthContext login:', authData);
    setToken(authData.token);
    setUser(authData.username);
    setAccount(authData.account);
    setRights(authData.rights);
    setProjection(authData.projection || null);
    // Only overwrite defaultCenter / cameraLock when the field is actually present in the
    // payload. On an F5 refresh, Init re-authenticates via /ping and routes the result
    // through login(); /ping historically omits these fields, so unconditionally setting
    // them to null would wipe the localStorage-hydrated values (the "lock dies on refresh"
    // bug). An explicit null still clears them; only `undefined` (absent) is preserved.
    if (authData.defaultCenter !== undefined) {
      setDefaultCenter(
        authData.defaultCenter
          ? { ...authData.defaultCenter, zoom: authData.defaultCenter.zoom ?? null }
          : null
      );
    }
    if (authData.cameraLock !== undefined) {
      setCameraLock(authData.cameraLock ?? null);
    }
    // Mirrors defaultCenter/cameraLock: only overwrite when the field is present, so the
    // /ping refresh path (which omits it) keeps the localStorage-hydrated value.
    if (authData.operatingGuide !== undefined) {
      setOperatingGuide(authData.operatingGuide ?? null);
    }

    logger.debug(`AuthContext: setting projection to ${authData.projection}`);

    // Store additional data in localStorage for persistence
    window.localStorage.setItem('username', authData.username);
    window.localStorage.setItem('rights', authData.rights);
  };

  /**
   * Log out user and clear all authentication data
   * Removes all user data from state and localStorage
   */
  const logout = (): void => {
    setToken(null);
    setUser(null);
    setAccount(null);
    setRights(null);
    setProjection(null);
    setDefaultCenter(null);
    setCameraLock(null);
    setOperatingGuide(null);
    setCameraLockOverride(false);
    setIsAuthenticated(false);

    // Clear all authentication-related data from localStorage
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('username');
    window.localStorage.removeItem('projection');
    window.localStorage.removeItem('rights');
    window.localStorage.removeItem('current-checkpoint');
  };

  /**
   * Update user data (for partial updates)
   */
  const updateUserData = (userData: AuthUserDataUpdate): void => {
    if (userData.username) setUser(userData.username);
    if (userData.account) setAccount(userData.account);
    if (userData.rights) setRights(userData.rights);
  };

  // Use useMemo to compute permissions that update when rights changes
  const computedPermissions = useMemo(
    () => ({
      isSuperAdmin: isSuperAdmin(),
      isAdmin: isAdmin(),
      isChecker: isChecker(),
      showFilters: showFilters(),
      canManageUsers: canManageUsers(),
      canAccessSystemConfig: canAccessSystemConfig(),
      canViewAuditLogs: canViewAuditLogs(),
      userRoleLevel: getUserRoleLevel(),
      userRoleName: getUserRoleName(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rights]
  );

  // Create context value object with all authentication data and methods
  const value: AuthContextValue = {
    // Authentication State
    token,
    user,
    account,
    rights,
    projection,
    defaultCenter,
    setDefaultCenter,
    cameraLock,
    setCameraLock,
    operatingGuide,
    setOperatingGuide,
    cameraLockOverride,
    setCameraLockOverride,
    isAuthenticated,

    // Authentication Actions
    login,
    logout,
    updateUserData,
    setToken,
    setProjection,

    // Computed Permission Properties (for easy access) - these update when rights changes
    ...computedPermissions,

    // Permission Helper Functions (for dynamic checks)
    isSuperAdminFn: isSuperAdmin,
    isAdminFn: isAdmin,
    isCheckerFn: isChecker,
    showFiltersFn: showFilters,
    canManageUsersFn: canManageUsers,
    canAccessSystemConfigFn: canAccessSystemConfig,
    canViewAuditLogsFn: canViewAuditLogs,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
